import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency, formatDate, getResponsiveThumbnailUrl } from '@/lib/utils';
import { Search, TrendingUp, Clock, Package } from 'lucide-react';
import { listingsAPI } from '@/lib/api';
import { Link } from 'react-router-dom';

const DashboardAllSalesTab = () => {
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        const fetchSales = async () => {
            try {
                // Fetch all direct sale listings (not auctions)
                const res = await listingsAPI.getAll({ listing_type: 'sale' });
                setSales(res.data?.listings || res.data || []);
            } catch (error) {
                console.error('Error fetching sales:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchSales();
    }, []);

    const filteredSales = sales.filter(s => 
        !searchQuery || 
        s.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.category?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-10 w-full" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[...Array(8)].map((_, i) => (
                        <Skeleton key={i} className="h-48" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <Card className="rounded-2xl">
            <CardHeader className="pb-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-green-500" />
                            Toate vânzările
                        </CardTitle>
                        <CardDescription>
                            {filteredSales.length} vânzări disponibile pe platformă
                        </CardDescription>
                    </div>
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Caută vânzări..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                        />
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {filteredSales.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                        <Package className="w-12 h-12 mx-auto mb-4 opacity-30" />
                        <p>Nu există vânzări disponibile momentan</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {filteredSales.map((sale) => (
                            <Link 
                                key={sale.listing_id} 
                                to={`/listing/${sale.slug || sale.listing_id}`}
                                className="block"
                            >
                                <Card className="hover:shadow-md transition-all hover:border-primary/30 cursor-pointer h-full">
                                    <CardContent className="p-3">
                                        {sale.images?.[0] && (
                                            <img
                                                src={getResponsiveThumbnailUrl(sale.images[0], 400, 200, 75)}
                                                alt={sale.title}
                                                className="w-full h-32 object-cover rounded-lg mb-3"
                                                loading="lazy"
                                            />
                                        )}
                                        <h3 className="font-medium text-sm truncate">{sale.title}</h3>
                                        <Badge variant="outline" className="mt-1 text-xs">
                                            {sale.category}
                                        </Badge>
                                        <div className="mt-2 flex items-center justify-between">
                                            <span className="font-semibold text-primary">
                                                {formatCurrency(sale.price)}
                                            </span>
                                            <Badge className={`text-xs ${
                                                sale.status === 'active' ? 'bg-green-100 text-green-800' :
                                                sale.status === 'sold' ? 'bg-blue-100 text-blue-800' :
                                                'bg-gray-100 text-gray-800'
                                            }`}>
                                                {sale.status === 'active' ? 'Disponibil' : 
                                                 sale.status === 'sold' ? 'Vândut' : sale.status}
                                            </Badge>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-2">
                                            Vânzător: {sale.seller_name || sale.seller_username}
                                        </p>
                                    </CardContent>
                                </Card>
                            </Link>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default DashboardAllSalesTab;
